import { useState, type ReactElement } from 'react';
import { container } from 'tsyringe';
import { Button, Group, Stack, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { UserPlus } from 'lucide-react';
import { UsersAdminService } from '@/features/admin/UsersAdminService';
import { StepShell } from './StepShell';

/**
 * Inline team-invite step — sends a single invite through the existing admin
 * users service (no navigation to the users screen). Optional; the wizard's
 * Next handles skipping.
 */
export function OnboardingTeamStep({ onInvited }: { onInvited: () => void }): ReactElement {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [busy, setBusy] = useState(false);

    const invite = async (): Promise<void> => {
        const trimmed = email.trim().toLowerCase();
        if (!trimmed) return;
        setBusy(true);
        try {
            await container.resolve(UsersAdminService).inviteUser({
                email: trimmed,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                roles: ['viewer'],
            });
            notifications.show({ color: 'green', message: `Invited ${trimmed}` });
            setEmail(''); setFirstName(''); setLastName('');
            onInvited();
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Invite failed' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <StepShell
            icon={<UserPlus size={20} />}
            title="Invite your team"
            description="Add a teammate now, or skip and invite people later from Settings."
        >
            <Stack gap="md">
                <TextInput
                    label="Email"
                    size="md"
                    placeholder="teammate@company.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void invite(); }}
                />
                <Group grow>
                    <TextInput label="First name" size="md" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
                    <TextInput label="Last name" size="md" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
                </Group>
                <Button fullWidth size="md" mt="xs" loading={busy} disabled={!email.trim()} onClick={() => void invite()}>
                    Send invite
                </Button>
            </Stack>
        </StepShell>
    );
}
